import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * Tracks whether any registered form has unsaved changes. Used by the tab
 * strip on the product detail page so we can prompt before navigating away.
 *
 * Usage:
 *   const dirty = useDirtyForm();
 *   useEffect(() => dirty.registerDirty("info", isDirty), [isDirty]);
 *
 *   // Before navigating:
 *   if (dirty.anyDirty && !(await dirty.confirmDiscard())) return;
 */
interface DirtyFormContextValue {
  registerDirty: (key: string, isDirty: boolean) => void;
  anyDirty: boolean;
  confirmDiscard: () => Promise<boolean>;
}

const DirtyFormContext = createContext<DirtyFormContextValue | null>(null);

export function DirtyFormProvider({ children }: { children: React.ReactNode }) {
  const [, force] = useState(0);
  const flags = useRef<Map<string, boolean>>(new Map());

  const registerDirty = useCallback((key: string, isDirty: boolean) => {
    const prev = flags.current.get(key);
    if (prev === isDirty) return;
    if (isDirty) flags.current.set(key, true);
    else flags.current.delete(key);
    force((n) => n + 1);
  }, []);

  const anyDirty = flags.current.size > 0;

  const confirmDiscard = useCallback(async () => {
    if (flags.current.size === 0) return true;
    return window.confirm("You have unsaved changes. Discard them and continue?");
  }, []);

  // Browser navigation guard while dirty
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (flags.current.size > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const value = useMemo(
    () => ({ registerDirty, anyDirty, confirmDiscard }),
    [registerDirty, anyDirty, confirmDiscard],
  );
  return <DirtyFormContext.Provider value={value}>{children}</DirtyFormContext.Provider>;
}

export function useDirtyForm(): DirtyFormContextValue {
  const ctx = useContext(DirtyFormContext);
  if (ctx) return ctx;
  // No provider — return a no-op stub so consumers don't crash on pages that
  // haven't wrapped themselves in the provider yet.
  return {
    registerDirty: () => {},
    anyDirty: false,
    confirmDiscard: async () => true,
  };
}
